import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { US_OFFERS_SCHEMA, US_OFFERS_SCHEMA_LEGACY } from '../schemas/UsOffers';
import { PromotionType } from '../types/offers';
import { formatDate } from '../utils/formatDate';
import { AS_OF_DATE, OFFER_MONTH } from '../constants/environment';
import { selectZipCode } from '../reducers/zipCodeReducer';
import { selectConfigFilters } from '../reducers/configFiltersReducer';

interface QueryResult {
  usCustomerOffers: PromotionType[];
}

// Use asOfDate schema if provided, otherwise fallback to legacy offerMonth schema
const useAsOfDate = !!AS_OF_DATE;
const schema = useAsOfDate ? US_OFFERS_SCHEMA : US_OFFERS_SCHEMA_LEGACY;

// Custom hook or function to handle the query logic
const useFetchOffers = (zipCode?: string) => {
  const { market } = useSelector(selectZipCode);
  const { hideLeaseOffers } = useSelector(selectConfigFilters);

  const isCaMarket = market === 'CA';

  // Build variables based on which param is available
  const variables = useAsOfDate
    ? { zipCode, asOfDate: AS_OF_DATE, hideLeaseOffers }
    : { zipCode, offerMonth: OFFER_MONTH, hideLeaseOffers };

  const { data, loading, error } = useQuery<
    QueryResult,
    {
      zipCode?: string;
      asOfDate?: string;
      offerMonth?: string;
      hideLeaseOffers?: boolean;
    }
  >(schema, {
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: isCaMarket,
    variables,
  });

  const offers =
    data?.usCustomerOffers &&
    data?.usCustomerOffers
      .map((offer) => {
        return {
          ...offer,
          offerType: offer.offerType.toLowerCase(),
          programToDate: offer?.programToDate ? formatDate(offer.programToDate) : '',
          programFromDate: offer?.programFromDate ? formatDate(offer.programFromDate) : '',
        };
      })
      .map((offer) => {
        const isSportbackBody = offer.offerModelDetail.modelCodeDescription.includes('Sportback');
        const isHybrid = offer.offerModelDetail.modelCodeDescription.includes('TFSI e');
        const isRWD = offer.offerModelDetail.modelCodeDescription.includes('RWD');
        const isFWD = offer.offerModelDetail.modelCodeDescription.includes('FWD');

        const correctDrivetrain = () => {
          if (isRWD) return 'rwd';
          if (isFWD) return 'fwd';
          return null;
        };

        const { bodyType } = offer.offerModelDetail;
        return {
          ...offer,
          offerModelDetail: {
            ...offer.offerModelDetail,
            bodyType: isSportbackBody ? 'Sportback' : bodyType,
            fuelType: isHybrid ? 'hybrid' : null,
            drivetrain: correctDrivetrain(),
          },
        };
      });

  return {
    loading,
    error,
    offers,
  };
};

export default useFetchOffers;
